import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, MapPin, Users, TrendingUp, TrendingDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/LanguageContext";

import atomBankLogo from "@/assets/logos/atom-bank.png";
import psdBankLogo from "@/assets/logos/psd-bank.png";
import raiffeisenLogo from "@/assets/logos/raiffeisen.webp";
import sparkasseLogo from "@/assets/logos/sparkasse-luebeck.png";

const bankMeta = [
  { id: "atom-bank", name: "Atom Bank", country: "UK", logo: atomBankLogo },
  { id: "psd-bank", name: "PSD Bank Braunschweig", country: "DE", logo: psdBankLogo },
  { id: "raiffeisenbank", name: "Raiffeisenbank Appenzeller Hinterland", country: "CH", logo: raiffeisenLogo },
  { id: "sparkasse-luebeck", name: "Sparkasse zu Lübeck", country: "DE", logo: sparkasseLogo },
];

function CaseCard({ meta, caseData, resultsLabel, isExpanded, onToggle }: {
  meta: typeof bankMeta[0];
  caseData: { type: string; highlight: string; model: string; results: string[]; quote?: { text: string; author: string; role: string } };
  resultsLabel: string;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <motion.div
      layout
      className={cn(
        "relative rounded-2xl border border-border bg-card overflow-hidden cursor-pointer transition-shadow",
        isExpanded ? "shadow-xl" : "shadow-sm hover:shadow-md"
      )}
      onClick={onToggle}
    >
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-white flex items-center justify-center w-14 h-14">
              <img src={meta.logo} alt={`${meta.name} Logo`} className="max-w-full max-h-full object-contain" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-foreground">{meta.name}</h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-3 w-3" />
                {meta.country}
              </div>
            </div>
          </div>
          <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          </motion.div>
        </div>

        <div className="inline-block px-3 py-1 rounded-full bg-accent/10 text-accent text-sm font-medium mb-3">
          {caseData.type}
        </div>

        <div className="flex items-center gap-2 text-lg font-semibold text-foreground">
          {meta.id === "atom-bank" ? (
            <TrendingDown className="h-5 w-5 text-red-600" />
          ) : (
            <TrendingUp className="h-5 w-5 text-report-success" />
          )}
          {caseData.highlight}
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-6 pb-6 border-t border-border pt-4">
              <p className="text-sm text-muted-foreground mb-4">{caseData.model}</p>

              <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Users className="h-4 w-4 text-accent" />
                {resultsLabel}
              </h4>
              <ul className="space-y-2 mb-4">
                {caseData.results.map((result, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <span className="text-accent mt-1">•</span>
                    {result}
                  </li>
                ))}
              </ul>

              {caseData.quote && (
                <blockquote className="border-l-4 border-accent pl-4 mt-4 bg-accent/5 py-3 rounded-r-lg">
                  <p className="text-sm italic text-foreground mb-2">„{caseData.quote.text}"</p>
                  <footer className="text-xs text-muted-foreground">
                    — {caseData.quote.author}, {caseData.quote.role}
                  </footer>
                </blockquote>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function CaseCards() {
  const [expandedId, setExpandedId] = useState<string | null>("atom-bank");
  const { t } = useLanguage();

  return (
    <section className="py-24 bg-muted/30">
      <div className="container px-4">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t.cases.title1}
            <br />
            {t.cases.title2}{" "}
            <span className="font-serif italic">{t.cases.titleItalic}</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.cases.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {bankMeta.map((meta, index) => (
            <motion.div
              key={meta.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <CaseCard
                meta={meta}
                caseData={t.cases.items[index]}
                resultsLabel={t.cases.resultsLabel}
                isExpanded={expandedId === meta.id}
                onToggle={() => setExpandedId(expandedId === meta.id ? null : meta.id)}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
